import { useEffect, useLayoutEffect, useReducer, useRef } from 'react';

import { isBrowser } from './isBrowser';

export type LazyProps = {
	ssrOnly?: boolean;
	whenVisible?: boolean | IntersectionObserverInit;
	children: React.ReactElement;
};

type Props = Omit<React.HTMLProps<HTMLElement>, 'dangerouslySetInnerHTML'> & LazyProps;

type VoidFunction = () => void;

// React currently throws a warning when using useLayoutEffect on the server.
const useIsomorphicLayoutEffect = isBrowser ? useLayoutEffect : useEffect;

function reducer() {
	return true;
}

export function LazyHydrate(props: Props) {
	const childRef = useRef<HTMLElement>(null);
	// Always render on server
	const [hydrated, hydrate] = useReducer(reducer, !isBrowser);

	const { ssrOnly, whenVisible, children, ...rest } = props;

	useIsomorphicLayoutEffect(() => {
		// No SSR Content
		if (!childRef.current?.hasChildNodes()) {
			hydrate();
		}
	}, []);

	useEffect(() => {
		if (ssrOnly || hydrated) return;
		const rootElement = childRef.current;

		const cleanupFns: VoidFunction[] = [];
		function cleanup() {
			cleanupFns.forEach((fn) => {
				fn();
			});
		}

		if (whenVisible) {
			const element = rootElement?.firstElementChild;
			if (element && typeof IntersectionObserver !== 'undefined') {
				const observerOptions =
					typeof whenVisible === 'object'
						? whenVisible
						: {
								rootMargin: '250px',
						  };

				const io = new IntersectionObserver((entries) => {
					entries.forEach((entry) => {
						if (entry.isIntersecting || entry.intersectionRatio > 0) {
							hydrate();
						}
					});
				}, observerOptions);

				io.observe(element);

				cleanupFns.push(() => {
					io.disconnect();
				});
			} else {
				return hydrate();
			}
		}

		return cleanup;
	}, [hydrated, ssrOnly, whenVisible]);

	const WrapperElement = 'div' as unknown as React.FC<React.HTMLProps<HTMLElement>>;

	if (hydrated) {
		return (
			<WrapperElement ref={childRef} style={{ display: 'contents' }} {...rest}>
				{children}
			</WrapperElement>
		);
	} else {
		return (
			<WrapperElement
				{...rest}
				ref={childRef}
				suppressHydrationWarning
				dangerouslySetInnerHTML={{ __html: '' }}
			/>
		);
	}
}

export default LazyHydrate;
